import { Injectable } from '@angular/core';

import { ignoreElementsTyped } from '@frontend/sports/common/core/utils/rxjs';
import { TrackingService, trackingConstants } from '@frontend/sports/tracking/feature';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NumpadTrackingService } from 'packages/sports/web/app/src/numpad/numpad-tracking.service';
import { UiManagerActions } from 'packages/sports/web/app/src/ui-manager/ui-manager.actions';
import { UiStates } from 'packages/sports/web/app/src/ui-manager/ui-states.enum';
import { filter, tap, withLatestFrom } from 'rxjs';

import { betslipSelector } from '../../../base/store/selectors';
import { IBetslipRootState } from '../../../base/store/state';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipPlaceBetActions } from '../../betplacement/actions';
import { PlaceButtonTracking, PlaceButtonTrackingService } from '../../betplacement/services/place-button/place-button-tracking.service';
import { betslipTypeStateSelector, editBetStateSelector } from '../../types/selectors';
import BetslipErrorsActions from '../../validation/actions';
import { UnderMinimumStakePreCheckError } from '../../validation/errors/pre-check/under-minimum-stake-pre-check-error';
import { NotEnoughMoney } from '../../validation/errors/user/not-enough-money';
import { getAllPickErrorTypes } from '../../validation/services/utils/betslip-errors-state-utils';
import { EditBetActions } from '../actions';

/* eslint-disable */

/* Disable type definitions for the epics, because it will be changed in redux-observable v1.0.0 */
@Injectable()
export class BetslipTrackingEffects {
    constructor(
        private actions$: Actions,
        private store: Store<IBetslipRootState>,
        private trackingService: TrackingService,
        private numpadTrackingService: NumpadTrackingService,
        private actionButtonTrackingService: PlaceButtonTrackingService,
    ) {}

    handleEditBetCancelClickedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.requestDiscard),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_Cancel_Clicked',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetAddSelectionClickedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.switchToAddSelectionMode),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_AddSelection_Clicked',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleAddSelectionScreenLoadedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(UiManagerActions.addState),
                filter((a) => a.state === UiStates.EditBetAddSelection),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_AddSelection_Screen_Loaded',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetAddSelectionOptionsBarColapseEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.togglePicksContainer),
                withLatestFrom(this.store.select(editBetStateSelector)),
                tap(([action, editMyBetState]) => {
                    const isExpanded = editMyBetState.addPicksState.isExpanded;
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: isExpanded
                            ? 'Sports_EditBet_AddSelection_OptionsBar_Expand'
                            : 'Sports_EditBet_AddSelection_OptionsBar_Colapse',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetAddSelectionAddToBetClickedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.confirmAddSelectionMode),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_AddSelection_AddToBet_Clicked',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetAddSelectionAddToBetCancelEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.cancelAddSelectionMode),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_AddSelection_AddToBet_Cancel',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetRemoveSelectionClickedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.removePick),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_RemoveSelection_Clicked',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetRemoveSelectionUndoEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.undoRemovePick),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_RemoveSelection_Undo',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetSwapScreenLoadedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.switchToSwapSelectionMode),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_Swap_Screen_Loaded',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetSwapConfirmedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.swapPick),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_Swap_Confirmed',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetAddStakeClickedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.startAddingStake),
                withLatestFrom(this.store.select(editBetStateSelector)),
                tap(([action, editMyBetState]) => {
                    const hasAddedStake = editMyBetState.addedStake;
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: hasAddedStake
                            ? 'Sports_EditBet_EditAddedStake_Clicked'
                            : 'Sports_EditBet_AddStake_Clicked',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleAddedStakeConfirmedEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.confirmAddingStake),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_AddStake_AddedtoEditSlip',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetSaveBetChangesClickEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.placeBet),
                tap((action) => {
                    this.trackingService.track(trackingConstants.EVENT_EDIT_BET, {
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_SaveBetChanges_Confirm',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetMakeDepositEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.makeDeposit),
                tap((action) => {
                    this.actionButtonTrackingService.track(PlaceButtonTracking.MakeDeposit, PlaceButtonTracking.EditMyBet);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetDepositEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(EditBetActions.deposit),
                tap((action) => {
                    this.actionButtonTrackingService.track(PlaceButtonTracking.Deposit, PlaceButtonTracking.EditMyBet);
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetFinishPlaceBetEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(BetslipPlaceBetActions.placeBetSucceeded),
                withLatestFrom(this.store.select(betslipTypeStateSelector)),
                filter(([action, typeState]) => typeState.base.currentSelectedType === BetslipType.EditBet),
                tap(([action, typeState]) => {
                    const state = typeState.editBet;

                    const numpadData = this.numpadTrackingService.getStakeSelectedData(
                        'Edit My Bet',
                        state.addedStake.toString(),
                        state.numpadAction,
                    );

                    this.trackingService.track('event.editedBet', {
                        ...numpadData,
                        [trackingConstants.PAGE_REFERRING_ACTION]: 'Sports_EditBet_SaveBetChanges_Successfull',
                        [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                    });
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetPickErrorsEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(BetslipErrorsActions.picksErrorsCheckFinished),
                withLatestFrom(this.store.select(betslipSelector)),
                tap(([action, betslipState]) => {
                    const { pickErrors } = betslipState.errors;
                    const errorTypes = getAllPickErrorTypes(pickErrors);
                    const isEditBet = betslipState.types.base.currentSelectedType === BetslipType.EditBet;
                    if (errorTypes.size > 0 && isEditBet) {
                        const track: string[] = [];
                        errorTypes.forEach((e) => track.push(e));
                        this.trackingService.track('editedBet_PreCheckError', {
                            [trackingConstants.PAGE_EDITBET_ERROR]: track.join(','),
                            [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                        });
                    }
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetStakeErrorEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(BetslipErrorsActions.betslipErrorsCheckFinished),
                withLatestFrom(this.store.select(betslipTypeStateSelector)),
                tap(([action, typeState]) => {
                    const error = action.errors.find((e) => e instanceof NotEnoughMoney || e instanceof UnderMinimumStakePreCheckError);
                    const isEditBet = typeState.base.currentSelectedType === BetslipType.EditBet;
                    if (error && isEditBet) {
                        this.trackingService.track('editedBet_PreCheckError', {
                            [trackingConstants.PAGE_EDITBET_ERROR]: error.type || 'UnknowPreCheckError',
                            [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                        });
                    }
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );

    handleEditBetPlaceBetErrorsEffect$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(BetslipErrorsActions.placeBetResponseErrors),
                withLatestFrom(this.store.select(betslipTypeStateSelector)),
                tap(([action, typeState]) => {
                    const betslipErrors = action.betslipErrors;
                    const pickErrorTypes = getAllPickErrorTypes(action.pickErrors);
                    const isEditBet = typeState.base.currentSelectedType === BetslipType.EditBet;
                    if ((pickErrorTypes.size || betslipErrors.length) && isEditBet) {
                        const track: string[] = [];
                        if (pickErrorTypes.size) {
                            pickErrorTypes.forEach((type) => track.push(type));
                        }
                        if (betslipErrors.length) {
                            track.push(betslipErrors[0].type);
                        }
                        this.trackingService.track('editedBet_Error', {
                            [trackingConstants.PAGE_EDITBET_ERROR]: track.join(','),
                            [trackingConstants.PAGE_SITE_SECTION]: 'myBets',
                        });
                    }
                }),
                ignoreElementsTyped(),
            ),
        { dispatch: false },
    );
}
